import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Form } from "react-bootstrap";
import { Snackbar, Alert, CircularProgress } from "@mui/material";
import { useDispatch } from "react-redux";
import { Input } from "Components/Input/Input";
import { Button } from "Components/Button/Button";
import { AuthUser } from "../../Services/login_service";
import { getPermisoRol } from "../../Services/permisos_service";
import { storageService } from "../../Services/storage_service";
import { setAuthUser } from "../../Slices/Login/loginSlice";
import { setPermisosAsignados } from "../../Slices/Permisos/permisosSlice";
import {
  setActiveDrawer,
  setShowNuevasCapacitacionesPresenciales,
} from "../../Slices/uiSlice";
import { useTokenExpiration } from "Hooks/useTokenExpiration";
import { useNotificationTimer } from "Hooks/useNotificationTimer";
import { useSyncronization } from "Hooks/useSyncronization";
import { useCursosPaquetes } from "Hooks/useCursosPaquetes";
import { getMisCapacitacionesPresenciales as getCapacitaciones } from "../../Services/mis_capacitaciones_presenciales_service";
import { useMutation } from "Hooks/useMutation";

const loginSchema = Yup.object({
  dpi: Yup.string()
    .required("El DPI es obligatorio")
    .matches(/^\d{13}$/, "El DPI debe tener exactamente 13 dígitos"),
  keepSession: Yup.boolean(),
});

export const LoginForm = ({ loading, setLoading }) => {
  const dispatch = useDispatch();
  const { startTokenTimer } = useTokenExpiration();
  const { startNotificationTimer } = useNotificationTimer();
  const { syncProgress, checkForSyncronization } = useSyncronization();
  const { refetch: refetchCursosPaquetes } = useCursosPaquetes();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });
  const { mutate: fetchCapacitaciones } = useMutation(getCapacitaciones);

  const formik = useFormik({
    initialValues: {
      dpi: "",
      keepSession: true,
    },
    validationSchema: loginSchema,
    onSubmit: async (values) => {
      await handleLogin(values);
    },
  });

  const handleLogin = async (values) => {
    setLoading(true);
    try {
      const loginData = {
        user: values.dpi,
        clave: values.dpi,
      };

      const resp = await AuthUser(loginData);

      if (resp.result === 0) {
        showSnackbar(resp.message, "error");
      } else {
        storageService.setItem("user", resp.data[0]);
        sessionStorage.setItem("user", JSON.stringify(resp.data[0]));
        startTokenTimer(resp.data[0]);
        const permisosRes = await getPermisoRol(resp.data[0]);
        dispatch(setPermisosAsignados(permisosRes));
        dispatch(setAuthUser(resp.data[0]));

        const capacitaciones = await fetchCapacitaciones(values.dpi);
        const capacitacionesNoMostradas =
          capacitaciones?.filter((cap) => !cap.mostrado) || [];

        const hasSync = checkForSyncronization();

        if (hasSync) {
          showSnackbar("Sincronizando progreso...", "info");
          await syncProgress(resp.data[0]);
        }

        showSnackbar("Inicio de sesión exitoso", "success");

        setTimeout(() => {
          dispatch(setActiveDrawer(null));
          startNotificationTimer();
          refetchCursosPaquetes();

          if (capacitacionesNoMostradas.length > 0) {
            dispatch(setShowNuevasCapacitacionesPresenciales(true));
          }
        }, 2000);
      }
    } catch (error) {
      console.error("Error en login:", error);
      showSnackbar("Error al iniciar sesión. Inténtalo de nuevo.", "error");
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleDpiChange = (e) => {
    const numbersOnly = e.target.value.replace(/\D/g, "").slice(0, 13);
    formik.setFieldValue("dpi", numbersOnly);
  };

  const handleDpiBlur = (e) => {
    if (formik.values.dpi.length > 0) {
      formik.handleBlur(e);
    }
  };

  return (
    <>
      <Form
        onSubmit={formik.handleSubmit}
        noValidate
        className="login-drawer__form"
      >
        <Input
          label="DPI"
          name="dpi"
          id="dpi"
          type="text"
          inputMode="numeric"
          placeholder="0000 00000 0000"
          value={formik.values.dpi}
          onChange={handleDpiChange}
          onBlur={handleDpiBlur}
          isInvalid={formik.touched.dpi && !!formik.errors.dpi}
          errorMessage={formik.touched.dpi ? formik.errors.dpi : ""}
          disabled={loading}
          maxLength={13}
          autoComplete="off"
          autoFocus
        />

        <Button
          type="submit"
          disabled={loading || !formik.values.dpi || !!formik.errors.dpi}
        >
          {loading && <CircularProgress size={20} sx={{ marginRight: 1 }} />}
          {loading ? "INICIANDO SESIÓN..." : "INICIAR SESIÓN"}
        </Button>
      </Form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
